import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { HumanAttentionBellIcon } from './HumanAttentionBellIcon';
import { useHumanAttentionOpenCount } from './useHumanAttentionOpenCount';
import { WORKFLOWS_UI_ENABLED } from '../../config/productFeatures';
import { showGrowl } from '../../ui/growlState';
import { CHANNEL_SIDEBAR_ORDER, channelLabel, channelSortKey } from './conversationChannelMeta';
import { ConversationChannelIcon } from './ConversationChannelIcon';
import { useTenantPermissions } from '../../auth/TenantPermissionsContext';
import { ConversationsAlertsStrip } from './notifications/ConversationsAlertsStrip';
import { useOpenOperatorNotifications } from './notifications/useOpenOperatorNotifications';
import { useAgentSelector } from '../../auth/AgentSelectorContext';

type ConvListMeta = {
  sentimentLabel: string;
  tokenTotal: number;
  /** Suma de tokens de entrada (prompt) en mensajes del agente con uso OpenAI. */
  tokenPromptTotal: number;
  /** Suma de tokens de salida (completion). */
  tokenCompletionTotal: number;
  /** Nombre del agente o flujo (consola), no el modelId. */
  providerLabel: string;
  /** modelId/etiqueta de API en la respuesta de agente más reciente; alinea con el informe de costo super admin. */
  lastAgentModel: string | null;
  /** Algún mensaje del agente usó reserva (modelo distinto al intentado). */
  hasModelFallback: boolean;
  lastActivityAt: string;
  lastMessagePreview: {
    senderLabel: string;
    content: string;
    timestamp: string;
  } | null;
  durationLabel: string;
  sessionInProgress: boolean;
  /** Abierta y duración menor a 5 min (desde creación hasta ahora). */
  isSessionLive: boolean;
  hasVisitor: boolean;
  /** Origen del hilo matchea la lista negra de contactos. */
  contactBlacklisted?: boolean;
};

type Conv = {
  id: string;
  channel: string;
  channelIntegrationId: string | null;
  status: string;
  controlMode: string;
  visitorName: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  virtualAgent: { id: string; name: string } | null;
  workflow: { id: string; name: string } | null;
  listMeta: ConvListMeta;
  leadQuality?: string | null;
  leadQualityLabel?: string | null;
  leadQualityColor?: { background: string; color: string } | null;
  leadRatedSource?: string | null;
  hasLeadQualificationPlugin?: boolean;
};

function truncateId(id: string): string {
  if (id.length <= 10) return id;
  return `${id.slice(0, 8)}…`;
}

function formatTimeShort(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '—';
  }
}

function ConversationListCard({ c }: { c: Conv }) {
  const m = c.listMeta;
  const preview = m.lastMessagePreview;
  const previewText = preview
    ? preview.content.replace(/\s+/g, ' ').trim()
    : 'Sin mensajes aún';

  const modeBg = c.controlMode === 'human' ? '#fef3c7' : 'rgba(22, 163, 74, 0.08)';
  const modeColor = c.controlMode === 'human' ? '#92400e' : '#16a34a';
  const modeLabel = c.controlMode === 'human' ? 'Humano' : 'Auto';

  return (
    <>
      {/* Row 1: Name + time */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
          {c.visitorName || truncateId(c.id)}
        </span>
        <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0, marginLeft: 8 }}>
          {formatTimeShort(m.lastActivityAt)}
        </span>
      </div>
      {/* Row 2: Channel + mode badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <ConversationChannelIcon channel={c.channel} size={14} />
        <span style={{ fontSize: 11, color: '#64748b' }}>{channelLabel(c.channel)}</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {m.contactBlacklisted ? (
            <span
              title="Lista negra"
              style={{
                padding: '2px 7px',
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 600,
                background: '#fef2f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
              }}
            >
              Lista negra
            </span>
          ) : null}
          <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: modeBg, color: modeColor }}>
            {modeLabel}
          </span>
          {c.hasLeadQualificationPlugin ? (
            <span
              title={c.leadQualityLabel || (c.leadQuality ? c.leadQuality : 'Lead sin marcar')}
              style={{
                padding: '2px 7px',
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 600,
                background: c.leadQualityColor?.background ?? (c.leadQuality === 'qualified' ? '#dcfce7' : c.leadQuality === 'unqualified' ? '#fee2e2' : '#f1f5f9'),
                color: c.leadQualityColor?.color ?? (c.leadQuality === 'qualified' ? '#15803d' : c.leadQuality === 'unqualified' ? '#b91c1c' : '#64748b'),
              }}
            >
              {c.leadQualityLabel || (c.leadQuality ? c.leadQuality : 'Lead')}
            </span>
          ) : null}
        </span>
      </div>
      {/* Row 3: Preview */}
      <div style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {previewText.length > 100 ? `${previewText.slice(0, 100)}…` : previewText}
      </div>
    </>
  );
}

const btnCardSettings: CSSProperties = {
  width: 32,
  height: 32,
  padding: 0,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#fff',
  cursor: 'pointer',
  fontSize: 16,
  lineHeight: 1,
  color: '#64748b',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const menuItemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  border: 'none',
  borderRadius: 6,
  background: 'transparent',
  fontSize: 13,
  color: '#0f172a',
  cursor: 'pointer',
};

function ConvCardSettings({
  c,
  onUpdated,
  token,
  hasContactBlacklist,
  canManageContactBlacklist,
}: {
  c: Conv;
  onUpdated: () => void;
  token: string;
  hasContactBlacklist: boolean;
  canManageContactBlacklist: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [contactBlocked, setContactBlocked] = useState(Boolean(c.listMeta?.contactBlacklisted));
  const menuWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setContactBlocked(Boolean(c.listMeta?.contactBlacklisted));
  }, [c.listMeta?.contactBlacklisted, c.id]);

  useEffect(() => {
    if (!hasContactBlacklist || !menuOpen) return;
    void apiGet<{ blocked: boolean }>(
      `/api/console/conversations/${c.id}/contact-blacklist/status`,
      token,
      { showErrorGrowl: false }
    )
      .then((d) => setContactBlocked(Boolean(d.blocked)))
      .catch(() => setContactBlocked(false));
  }, [hasContactBlacklist, menuOpen, c.id, token]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  useEffect(() => {
    if (!tagOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [tagOpen]);

  useEffect(() => {
    if (!tagOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) setTagOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tagOpen, busy]);

  const saveTags = async () => {
    const tags = tagDraft
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 32);
    setBusy(true);
    try {
      await apiPatch(`/api/console/conversations/${c.id}`, { tags }, token, { successMessage: 'Etiquetas guardadas' });
      setTagOpen(false);
      onUpdated();
    } catch (e) {
      showGrowl((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div ref={menuWrapRef} style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label="Opciones de conversación"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title="Opciones"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
          style={btnCardSettings}
        >
          ⚙
        </button>
        {menuOpen ? (
          <div
            role="menu"
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: 4,
              zIndex: 50,
              minWidth: 220,
              padding: 4,
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
            }}
          >
            <button
              type="button"
              role="menuitem"
              style={menuItemStyle}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                setTagDraft(c.tags.length ? c.tags.join(', ') : '');
                setTagOpen(true);
              }}
            >
              Agregar o editar etiquetas…
            </button>
            {hasContactBlacklist && canManageContactBlacklist ? (
              <button
                type="button"
                role="menuitem"
                style={menuItemStyle}
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  void (async () => {
                    setBusy(true);
                    try {
                      if (contactBlocked) {
                        await apiPost(
                          `/api/console/conversations/${c.id}/contact-blacklist/remove`,
                          {},
                          token,
                          { successMessage: 'Quitado de la lista negra' }
                        );
                        setContactBlocked(false);
                      } else {
                        await apiPost(
                          `/api/console/conversations/${c.id}/contact-blacklist`,
                          { scope: 'thread' },
                          token,
                          { successMessage: 'Agregado a la lista negra' }
                        );
                        setContactBlocked(true);
                      }
                      setMenuOpen(false);
                      onUpdated();
                    } catch (err) {
                      showGrowl((err as Error).message, 'error');
                    } finally {
                      setBusy(false);
                    }
                  })();
                }}
              >
                {contactBlocked ? 'Quitar de lista negra' : 'Agregar a lista negra'}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {tagOpen
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="tag-modal-title"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
                boxSizing: 'border-box',
                background: 'rgba(15, 23, 42, 0.55)',
              }}
              onClick={() => {
                if (!busy) setTagOpen(false);
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: 420,
                  width: '100%',
                  padding: 20,
                  borderRadius: 12,
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.2)',
                }}
              >
                <h2 id="tag-modal-title" style={{ fontSize: 16, margin: '0 0 6px', fontWeight: 700, color: '#0f172a' }}>
                  Etiquetas
                </h2>
                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px', lineHeight: 1.45 }}>
                  Separá con comas. Máximo 32 etiquetas. Se reemplazan las de esta conversación.
                </p>
                <label style={{ display: 'block', fontSize: 12, color: '#475569', fontWeight: 600 }}>
                  Etiquetas
                  <textarea
                    value={tagDraft}
                    onChange={(e) => setTagDraft(e.target.value)}
                    disabled={busy}
                    placeholder="ej. lead, urgente, follow-up"
                    rows={3}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      marginTop: 6,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      fontSize: 14,
                      fontFamily: 'inherit',
                      resize: 'vertical' as const,
                    }}
                  />
                </label>
                <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setTagOpen(false)}
                    style={{
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: busy ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void saveTags()}
                    style={{
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: '#7c3aed',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: busy ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {busy ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function ConversationListItem({
  c,
  onTagsUpdated,
  token,
  alertBackgroundColor,
  hasContactBlacklist,
  canManageContactBlacklist,
}: {
  c: Conv;
  onTagsUpdated: () => void;
  token: string;
  alertBackgroundColor?: string;
  hasContactBlacklist: boolean;
  canManageContactBlacklist: boolean;
}) {
  const loc = useLocation();
  const isActive = loc.pathname === `/app/conversations/${c.id}`;
  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        alignItems: 'flex-start',
        padding: '12px 14px',
        borderRadius: 10,
        borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
        border: `1px solid ${isActive ? '#c4b5fd' : '#f1f5f9'}`,
        background: isActive ? '#f5f3ff' : alertBackgroundColor || '#fff',
        transition: 'background 0.12s ease',
      }}
    >
      <NavLink
        to={`/app/conversations/${c.id}`}
        style={{ flex: 1, minWidth: 0, textDecoration: 'none', color: '#0f172a', display: 'block' }}
      >
        <ConversationListCard c={c} />
      </NavLink>
      <ConvCardSettings
        c={c}
        onUpdated={onTagsUpdated}
        token={token}
        hasContactBlacklist={hasContactBlacklist}
        canManageContactBlacklist={canManageContactBlacklist}
      />
    </div>
  );
}

type Agent = { id: string; name: string; isActive: boolean };
type WorkflowOpt = { id: string; name: string; isActive: boolean };

type ConvListResponse = { items: Conv[]; nextCursor: string | null };

const PAGE_SIZE = 50;

export function ConversationsLayout() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY)!;
  const { can } = useTenantPermissions();
  const { selectedAgentId } = useAgentSelector();
  const canReadNotifications = can('web.notifications.read');
  const canManageContactBlacklist = can('web.conversations.write');
  const nav = useNavigate();
  const loc = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterHumanOnly = searchParams.get('human') === '1';
  const leadQualityFilter = searchParams.get('leadQuality') ?? '';
  const [leadFilterOptions, setLeadFilterOptions] = useState<Array<{ id: string; label: string }>>([
    { id: 'qualified', label: 'Calificados' },
    { id: 'unqualified', label: 'No calificados' },
  ]);
  const [items, setItems] = useState<Conv[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowOpt[]>([]);
  const [channelsFromApi, setChannelsFromApi] = useState<string[]>([]);
  const [err, setErr] = useState('');
  const [visitorName, setVisitorName] = useState('');
  const [agentId, setAgentId] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [listTick, setListTick] = useState(0);
  const [channelFilter, setChannelFilter] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(340);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hasContactBlacklist, setHasContactBlacklist] = useState(false);
  const listGenRef = useRef(0);
  const loadMoreRef = useRef<() => void>(() => {});

  const { count: humanAttentionOpenCount, refresh: refreshHumanAttentionOpenCount } =
    useHumanAttentionOpenCount(selectedAgentId);

  const {
    openItems: openAlertItems,
    alertColorByConversationId,
    refresh: refreshOpenAlerts,
    loading: alertsLoading,
  } = useOpenOperatorNotifications(canReadNotifications);

  const toggleHumanAttentionFilter = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get('human') === '1') next.delete('human');
        else next.set('human', '1');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const refreshList = useCallback(() => {
    setListTick((t) => t + 1);
    void (async () => {
      try {
        const qs = selectedAgentId ? `?agentId=${encodeURIComponent(selectedAgentId)}` : '';
        const ch = await apiGet<string[]>(`/api/console/conversations/channels${qs}`, token);
        setChannelsFromApi(ch);
      } catch {
        /* list sigue válida sin actualizar canales */
      }
    })();
  }, [token, selectedAgentId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const qs = selectedAgentId ? `?agentId=${encodeURIComponent(selectedAgentId)}` : '';
    void apiGet<{ hasPlugin: boolean }>(`/api/console/contact-blacklist/has-plugin${qs}`, token, { showErrorGrowl: false })
      .then((d) => setHasContactBlacklist(Boolean(d.hasPlugin)))
      .catch(() => setHasContactBlacklist(false));
  }, [token, selectedAgentId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [a, ch, w] = await Promise.all([
          apiGet<Agent[]>('/api/console/agents', token),
          apiGet<string[]>(
            `/api/console/conversations/channels${selectedAgentId ? `?agentId=${encodeURIComponent(selectedAgentId)}` : ''}`,
            token
          ),
          WORKFLOWS_UI_ENABLED
            ? apiGet<WorkflowOpt[]>('/api/console/workflows', token)
            : Promise.resolve([] as WorkflowOpt[]),
        ]);
        if (cancelled) return;
        setAgents(a.filter((x) => x.isActive));
        setWorkflows(w);
        setChannelsFromApi(ch);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, selectedAgentId]);

  // Al cambiar de agente, cerrar el chat abierto (puede ser de otro agente).
  const prevSelectedAgentRef = useRef(selectedAgentId);
  useEffect(() => {
    if (prevSelectedAgentRef.current === selectedAgentId) return;
    prevSelectedAgentRef.current = selectedAgentId;
    if (/^\/app\/conversations\/[^/]+/.test(loc.pathname)) {
      nav('/app/conversations', { replace: true });
    }
  }, [selectedAgentId, loc.pathname, nav]);

  useEffect(() => {
    const gen = ++listGenRef.current;
    let cancelled = false;
    async function run() {
      setLoadingList(true);
      setErr('');
      try {
        const params = new URLSearchParams();
        params.set('limit', String(PAGE_SIZE));
        if (channelFilter) params.set('channel', channelFilter);
        if (debouncedSearch) params.set('q', debouncedSearch);
        if (filterHumanOnly) params.set('human', '1');
        if (leadQualityFilter) params.set('leadQuality', leadQualityFilter);
        if (selectedAgentId) params.set('agentId', selectedAgentId);
        const data = await apiGet<ConvListResponse>(
          `/api/console/conversations?${params.toString()}`,
          token
        );
        if (cancelled || gen !== listGenRef.current) return;
        setItems(data.items);
        setNextCursor(data.nextCursor);
      } catch (e) {
        if (!cancelled && gen === listGenRef.current) setErr((e as Error).message);
      } finally {
        if (!cancelled && gen === listGenRef.current) setLoadingList(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, listTick, channelFilter, debouncedSearch, filterHumanOnly, leadQualityFilter, selectedAgentId]);

  useEffect(() => {
    const qs = selectedAgentId ? `?agentId=${encodeURIComponent(selectedAgentId)}` : '';
    void apiGet<{ options: Array<{ id: string; label: string }> }>(`/api/console/lead-qualification/filters${qs}`, token)
      .then((data) => {
        if (Array.isArray(data.options) && data.options.length > 0) setLeadFilterOptions(data.options);
      })
      .catch(() => {});
  }, [token, selectedAgentId]);

  useEffect(() => {
    void refreshHumanAttentionOpenCount();
  }, [listTick, refreshHumanAttentionOpenCount]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore || loadingList) return;
    const genAtStart = listGenRef.current;
    setLoadingMore(true);
    setErr('');
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('cursor', nextCursor);
      if (channelFilter) params.set('channel', channelFilter);
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (filterHumanOnly) params.set('human', '1');
      if (leadQualityFilter) params.set('leadQuality', leadQualityFilter);
      if (selectedAgentId) params.set('agentId', selectedAgentId);
      const data = await apiGet<ConvListResponse>(
        `/api/console/conversations?${params.toString()}`,
        token
      );
      if (genAtStart !== listGenRef.current) return;
      setItems((prev) => [...prev, ...data.items]);
      setNextCursor(data.nextCursor);
    } catch (e) {
      if (genAtStart === listGenRef.current) setErr((e as Error).message);
    } finally {
      if (genAtStart === listGenRef.current) setLoadingMore(false);
    }
  }, [
    nextCursor,
    loadingMore,
    loadingList,
    channelFilter,
    debouncedSearch,
    filterHumanOnly,
    leadQualityFilter,
    selectedAgentId,
    token,
  ]);

  loadMoreRef.current = () => {
    void loadMore();
  };

  const onListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 120) return;
    loadMoreRef.current();
  }, []);

  const grouped = useMemo(() => {
    const by = new Map<string, Conv[]>();
    for (const c of items) {
      const k = c.channel;
      if (!by.has(k)) by.set(k, []);
      by.get(k)!.push(c);
    }
    for (const list of by.values()) {
      list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    const keys = Array.from(by.keys()).sort((a, b) => {
      const da = channelSortKey(a);
      const db = channelSortKey(b);
      if (da !== db) return da - db;
      return a.localeCompare(b);
    });
    return { by, keys };
  }, [items]);

  const channelOptions = useMemo(() => {
    const present = new Set(channelsFromApi);
    const ordered = CHANNEL_SIDEBAR_ORDER.filter((c) => present.has(c));
    const rest = [...present]
      .filter((c) => !CHANNEL_SIDEBAR_ORDER.includes(c as (typeof CHANNEL_SIDEBAR_ORDER)[number]))
      .sort((a, b) => a.localeCompare(b));
    return [...ordered, ...rest];
  }, [channelsFromApi]);

  const filteredKeys = useMemo(() => {
    if (!channelFilter) return grouped.keys;
    return grouped.keys.filter((k) => k === channelFilter);
  }, [grouped.keys, channelFilter]);

  const createTestchat = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    try {
      const data = await apiPost<{ id: string }>(
        '/api/console/conversations',
        {
          visitorName: visitorName.trim() || undefined,
          virtualAgentId: workflowId ? null : agentId || null,
          workflowId: workflowId || null,
          channel: 'testchat',
        },
        token,
        { successMessage: 'Conversación de prueba creada' }
      );
      setVisitorName('');
      setWorkflowId('');
      refreshList();
      nav(`/app/conversations/${data.id}`);
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        height: 'calc(100vh - 52px)',
        overflow: 'hidden',
        margin: 0,
      }}
    >
      <aside
        style={{
          width: sidebarWidth,
          minWidth: 240,
          maxWidth: 600,
          flexShrink: 0,
          borderRight: '1px solid #e2e8f0',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          position: 'relative',
        }}
      >
        {/* Resize handle */}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startWidth = sidebarWidth;
            const onMove = (ev: MouseEvent) => {
              const newWidth = Math.min(600, Math.max(240, startWidth + (ev.clientX - startX)));
              setSidebarWidth(newWidth);
            };
            const onUp = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
              document.body.style.cursor = '';
              document.body.style.userSelect = '';
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
          }}
          style={{
            position: 'absolute',
            top: 0,
            right: -3,
            width: 6,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 10,
            background: 'transparent',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(99, 102, 241, 0.15)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          title="Arrastrar para redimensionar"
        />
        <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Conversaciones</h1>
              {humanAttentionOpenCount > 0 || filterHumanOnly ? (
                <HumanAttentionBellIcon
                  size={17}
                  active={filterHumanOnly}
                  onClick={toggleHumanAttentionFilter}
                  title={
                    filterHumanOnly
                      ? 'Filtro activo: solo conversaciones abiertas en modo humano. Clic para ver todas.'
                      : humanAttentionOpenCount === 1
                        ? 'Hay 1 conversación abierta en modo humano. Clic para mostrar solo esas.'
                        : `Hay ${humanAttentionOpenCount} conversaciones abiertas en modo humano. Clic para mostrar solo esas.`
                  }
                />
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                color: filtersOpen ? '#7c3aed' : '#64748b',
                transition: 'color 0.15s, background 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              title={filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: 4 }}>
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Filtros
            </button>
          </div>
          {filtersOpen && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <select
                style={{ ...inpSm, fontSize: 12 }}
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value)}
              >
                <option value="">Todos los canales</option>
                {channelOptions.map((ch) => (
                  <option key={ch} value={ch}>
                    {channelLabel(ch)}
                  </option>
                ))}
              </select>
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar por nombre, ID, tag…"
                style={{ ...inpSm, fontSize: 12 }}
                autoComplete="off"
              />
              <select
                style={{ ...inpSm, fontSize: 12 }}
                value={leadQualityFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = new URLSearchParams(searchParams);
                  if (v) next.set('leadQuality', v);
                  else next.delete('leadQuality');
                  setSearchParams(next, { replace: true });
                }}
              >
                <option value="">Todos los leads</option>
                <option value="unmarked">Sin marcar</option>
                {leadFilterOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {canReadNotifications ? (
          <ConversationsAlertsStrip
            items={openAlertItems}
            loading={alertsLoading}
            onChanged={() => void refreshOpenAlerts()}
          />
        ) : null}

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }} onScroll={onListScroll}>
          {loadingList ? (
            <p style={{ padding: 16, fontSize: 13, color: '#64748b' }}>Cargando conversaciones…</p>
          ) : items.length === 0 ? (
            <p style={{ padding: 16, fontSize: 13, color: '#94a3b8' }}>
              {filterHumanOnly && !channelFilter && !debouncedSearch
                ? 'No hay conversaciones abiertas en modo humano.'
                : filterHumanOnly && (channelFilter || debouncedSearch)
                  ? 'Ninguna conversación coincide con la atención humana y los filtros actuales.'
                  : channelFilter && !debouncedSearch
                    ? 'No hay conversaciones en este canal.'
                    : !debouncedSearch && !channelFilter && channelsFromApi.length === 0
                      ? 'Aún no hay conversaciones.'
                      : 'Ninguna conversación coincide con la búsqueda o el filtro.'}
            </p>
          ) : filteredKeys.length === 0 ? (
            <p style={{ padding: 16, fontSize: 13, color: '#94a3b8' }}>
              No hay conversaciones en este canal.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: '4px 10px 10px' }}>
              {items
                .filter((c) => !channelFilter || c.channel === channelFilter)
                .map((c) => (
                  <li key={c.id} style={{ marginBottom: 8, listStyle: 'none' }}>
                    <ConversationListItem
                      c={c}
                      onTagsUpdated={refreshList}
                      token={token}
                      alertBackgroundColor={alertColorByConversationId.get(c.id)}
                      hasContactBlacklist={hasContactBlacklist}
                      canManageContactBlacklist={canManageContactBlacklist}
                    />
                  </li>
                ))}
            </ul>
          )}
          {loadingMore ? (
            <p style={{ padding: '8px 16px 16px', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
              Cargando más…
            </p>
          ) : null}
          {!loadingList && !loadingMore && items.length > 0 && !nextCursor ? (
            <p style={{ padding: '4px 16px 12px', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
              Fin de la lista
            </p>
          ) : null}
        </div>

        <div
          style={{
            borderTop: '1px solid #e2e8f0',
            padding: '10px 12px',
            background: '#fafafa',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: '#7c3aed',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              width: '100%',
              justifyContent: 'center',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#6d28d9')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#7c3aed')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
              <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
            </svg>
            Nueva prueba
          </button>
        </div>
      </aside>

      {/* Drawer overlay + panel */}
      {drawerOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {/* Overlay */}
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.5)',
              backdropFilter: 'blur(2px)',
              transition: 'opacity 0.2s ease',
            }}
          />
          {/* Panel */}
          <div
            style={{
              position: 'relative',
              width: 360,
              maxWidth: '90vw',
              height: '100%',
              backgroundColor: '#ffffff',
              boxShadow: '-8px 0 30px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideInRight 0.25s ease forwards',
            }}
          >
            {/* Drawer header */}
            <div
              style={{
                padding: '20px 24px 16px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                  Nueva Prueba
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                  Crear conversación de testchat
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 8,
                  borderRadius: 8,
                  color: '#64748b',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                aria-label="Cerrar"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            {/* Drawer body */}
            <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
              <form onSubmit={(e) => { void createTestchat(e); setDrawerOpen(false); }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Nombre del visitante
                </label>
                <input
                  style={{ ...inpSm, padding: '10px 12px', fontSize: 14, borderRadius: 8 }}
                  placeholder="Nombre (opcional)"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  autoFocus
                />

                {WORKFLOWS_UI_ENABLED ? (
                  <>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginTop: 20, marginBottom: 6 }}>
                      Flujo
                    </label>
                    <select
                      style={{ ...inpSm, padding: '10px 12px', fontSize: 14, borderRadius: 8 }}
                      value={workflowId}
                      onChange={(e) => {
                        setWorkflowId(e.target.value);
                        if (e.target.value) setAgentId('');
                      }}
                    >
                      <option value="">Sin flujo</option>
                      {workflows.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                          {!w.isActive ? ' (inactivo)' : ''}
                        </option>
                      ))}
                    </select>
                  </>
                ) : null}

                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#334155',
                    marginTop: 20,
                    marginBottom: 6,
                  }}
                >
                  Agente
                </label>
                <select
                  style={{ ...inpSm, padding: '10px 12px', fontSize: 14, borderRadius: 8 }}
                  value={agentId}
                  onChange={(e) => {
                    setAgentId(e.target.value);
                    if (e.target.value) setWorkflowId('');
                  }}
                  disabled={WORKFLOWS_UI_ENABLED ? Boolean(workflowId) : false}
                >
                  <option value="">Sin agente</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    marginTop: 28,
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#7c3aed',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: 'pointer',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#6d28d9')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#7c3aed')}
                >
                  Crear prueba
                </button>

                {WORKFLOWS_UI_ENABLED ? (
                  <p style={{ fontSize: 12, color: '#64748b', margin: '16px 0 0', lineHeight: 1.5 }}>
                    Los flujos <strong>inactivos</strong> aparecen igual: en testchat se ejecutan para probar; en canales
                    reales hay que activarlos en el editor.
                  </p>
                ) : null}
              </form>
              {err ? <p style={{ color: '#b91c1c', fontSize: 12, margin: '12px 0 0' }}>{err}</p> : null}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0.8;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      <main style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Outlet context={{ refreshList }} />
      </main>
    </div>
  );
}

const inpSm: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  fontSize: 13,
};

